import { useState, useRef, useEffect, useLayoutEffect, useCallback, type CSSProperties } from "react";
import { POPUP_OFFSET_TOP, POPUP_OFFSET_LEFT } from "@RequestFormV2/sections/PackingList/components/shared/cratingDatePickerUtils";

export default function useDatePickerPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  const getPopupStyle = useCallback((): CSSProperties | null => {
    const anchor = containerRef.current;
    if (!anchor) return null;

    const { left: anchorLeft, bottom } = anchor.getBoundingClientRect();
    const popupWidth = popupRef.current?.offsetWidth ?? 320;
    let left = anchorLeft + POPUP_OFFSET_LEFT;
    left = Math.min(left, window.innerWidth - popupWidth - 8);
    left = Math.max(8, left);

    return {
      position: "fixed",
      top: bottom + POPUP_OFFSET_TOP,
      left,
      zIndex: 10000,
    };
  }, []);

  const updatePopupPosition = useCallback(() => {
    const nextStyle = getPopupStyle();
    if (nextStyle) setPopupStyle(nextStyle);
  }, [getPopupStyle]);

  const closePopup = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openPopup = useCallback(() => {
    const nextStyle = getPopupStyle();
    if (nextStyle) setPopupStyle(nextStyle);
    setIsOpen(true);
  }, [getPopupStyle]);

  const setPopupRef = useCallback(
    (node: HTMLDivElement | null) => {
      popupRef.current = node;
      if (node) updatePopupPosition();
    },
    [updatePopupPosition],
  );

  useLayoutEffect(() => {
    if (!isOpen) return;

    updatePopupPosition();
    const rafId = window.requestAnimationFrame(updatePopupPosition);

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [isOpen, updatePopupPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    window.addEventListener("resize", updatePopupPosition);
    window.addEventListener("scroll", updatePopupPosition, true);

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      closePopup();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("resize", updatePopupPosition);
      window.removeEventListener("scroll", updatePopupPosition, true);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [closePopup, isOpen, updatePopupPosition]);

  return { isOpen, popupStyle, containerRef, popupRef, setPopupRef, openPopup, closePopup };
}
